import { sessionsApi } from '../api/sessions';

/**
 * First chat load after Start this book can miss. Recover into the
 * opener instead of leaving the writer on Failed to fetch (#848).
 * Does not spawn a second book.
 */
export const CHAT_LOAD_RETRIES = 4;
export const CHAT_LOAD_RETRY_MS = 250;

export function isTransientChatLoadMiss(err) {
  if (!err) return false;
  if (err.status === 401 || err.status === 403) return false;
  if (err.status === 404) return true;
  const message = String(err.message || '').toLowerCase();
  if (message === 'failed to fetch') return true;
  if (message.includes('network')) return true;
  return err.name === 'TypeError';
}

export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function withChatLoadRetry(load, { retries = CHAT_LOAD_RETRIES, pause } = {}) {
  const wait = pause || sleep;
  let lastErr;
  for (let attempt = 0; attempt < retries; attempt += 1) {
    try {
      return await load();
    } catch (err) {
      lastErr = err;
      if (!isTransientChatLoadMiss(err) || attempt === retries - 1) {
        throw err;
      }
      await wait(CHAT_LOAD_RETRY_MS * (attempt + 1));
    }
  }
  throw lastErr;
}

/**
 * One quiet extra attempt for a dropped connection. The session read
 * already retries inside loadChatSession; the story and project reads
 * that follow it do not.
 */
export async function withOneQuietRetry(load, { pause } = {}) {
  const wait = pause || sleep;
  try {
    return await load();
  } catch (err) {
    if (!isTransientChatLoadMiss(err)) throw err;
    await wait(CHAT_LOAD_RETRY_MS);
    return load();
  }
}

export function loadChatSession(sessionId, options = {}) {
  const getSession = options.getSession || ((id) => sessionsApi.get(id, {
    signal: options.signal,
  }));
  return withChatLoadRetry(() => getSession(sessionId), options);
}
